import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AlertController, ToastController } from '@ionic/angular';
import { NaploService } from '../naplo.service';

/**
 *  NAPLO – Szinkronizálás. Két szinten állítható:
 *
 *    GLOBÁLIS (intézményi) – a forrás minden ügyfélnél bekapcsol. Ez az alapértelmezés: egy
 *    könyvelőirodánál nem életszerű ügyfelenként nyolc kapcsolót végigkattintani, és új
 *    ügyfélnél amúgy is elmaradna a bekapcsolás.
 *
 *    ÜGYFÉL-SZINTŰ – felülírja a globálisat (bekapcsolva / kikapcsolva / „globális szerint”).
 *
 *  Az áttekintő táblázat megmutatja, hol nincs meg a forráshoz szükséges hitelesítő (NAV
 *  technikai felhasználó, M2M-kapcsolat, Ügyfélkapu-fiók, banki meghatalmazás): ott a rendszer
 *  el sem indítja a lekérést.
 */
@Component({
  selector: 'app-naplo-szinkron',
  templateUrl: './szinkron.component.html',
  styleUrls: ['../naplo-shared.scss', './szinkron.component.scss'],
})
export class SzinkronComponent implements OnInit {
  private readonly base = '/api/naplo-opg';
  private readonly gbase = '/api/naplo-szinkron';
  get aktiv() { return this.naplo.aktiv; }

  nezet: 'globalis' | 'ugyfel' = 'globalis';
  szinkron: any[] = [];          // az aktív ügyfél sorai (hatályos értékekkel)
  globalis: any[] = [];          // intézményi alapbeállítások + statisztika
  attekintes: any[] = [];        // ügyfél × forrás hatályos állapot
  szuroForras = '';
  csakProblemas = false;
  tolt = false;

  forrasCimke: Record<string, string> = { osa: 'OSA / NAV számla', opg: 'Pénztárgép (OPG)', onkormanyzat: 'e-Önkormányzat', tarhely: 'Tárhely', nav: 'NAV Számlázó', adofolyoszamla: 'Adófolyószámla (NAV M2M)', bank: 'Bankszinkron', torzsadat: 'NAV törzsadat', szamlazo: 'Számlaképek (Billingo / Számlázz.hu)' };
  forrasIkon: Record<string, string> = { osa: 'receipt', opg: 'calculator', onkormanyzat: 'business', tarhely: 'mail-unread', nav: 'document-text', adofolyoszamla: 'server', bank: 'card', torzsadat: 'id-card', szamlazo: 'images' };

  constructor(private naplo: NaploService, private http: HttpClient, private toast: ToastController, private alert: AlertController) {}

  private get<T = any>(p: string) { return firstValueFrom(this.http.get<T>(this.base + p)); }
  private put<T = any>(p: string, b: any) { return firstValueFrom(this.http.put<T>(this.base + p, b)); }
  private gget<T = any>(p: string) { return firstValueFrom(this.http.get<T>(this.gbase + p)); }
  private gput<T = any>(p: string, b: any) { return firstValueFrom(this.http.put<T>(this.gbase + p, b)); }
  private gpost<T = any>(p: string, b: any) { return firstValueFrom(this.http.post<T>(this.gbase + p, b)); }
  private gdel<T = any>(p: string) { return firstValueFrom(this.http.delete<T>(this.gbase + p)); }

  async ngOnInit() {
    await this.naplo.init();
    await this.globalisBetolt();
    if (this.aktiv.ugyfelId) await this.betolt();
  }

  async betolt() {
    if (!this.aktiv.ugyfelId) { this.szinkron = []; return; }
    try { this.szinkron = await this.get(`/szinkron-beallitas/${this.aktiv.ugyfelId}`); } catch { this.szinkron = []; }
  }

  async globalisBetolt() {
    if (!this.aktiv.intezmeny) return;
    this.tolt = true;
    try {
      const r: any = await this.gget(`/attekintes/${this.aktiv.intezmeny}`);
      this.globalis = r?.forrasok || [];
      this.attekintes = r?.sorok || [];
    } catch { this.globalis = []; this.attekintes = []; }
    this.tolt = false;
  }

  /* ── globális (intézményi) beállítás ── */
  async globalisMent(g: any) {
    try {
      await this.gput(`/globalis/${g.forras}/${this.aktiv.intezmeny}`, { enabled: g.enabled ? 1 : 0, intervallum_perc: g.intervallum_perc });
      await this.globalisBetolt(); await this.betolt();
      this.ok(`${this.forrasCimke[g.forras] || g.forras}: ${g.enabled ? 'bekapcsolva' : 'kikapcsolva'} minden ügyfélnél (a saját beállítást kivéve).`);
    } catch { this.hiba('Nem sikerült menteni a globális beállítást.'); await this.globalisBetolt(); }
  }

  /** Egy forrás összes ügyfél-szintű felülírásának törlése – mindenki a globálisat követi. */
  async globalisra(g: any) {
    if (!g.felulirt_db) return;
    const a = await this.alert.create({
      header: 'Vissza a globálisra',
      message: `${g.felulirt_db} ügyfélnél van saját beállítás a(z) „${this.forrasCimke[g.forras] || g.forras}" forrásra. Ezek törlődnek, és mindenki az intézményi beállítást követi.`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Visszaállítás', role: 'confirm' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'confirm') return;
    try {
      const r: any = await this.gpost(`/globalisra/${g.forras}/${this.aktiv.intezmeny}`, {});
      await this.globalisBetolt(); await this.betolt();
      this.ok(`${r?.erintett || 0} ügyfél állt vissza a globális beállításra.`);
    } catch { this.hiba('Nem sikerült visszaállítani.'); }
  }

  /* ── ügyfél-szintű beállítás (felülírás) ── */
  /** A hármas kapcsoló értéke: 'globalis' | 'be' | 'ki'. */
  allapot(s: any): string { return s.sajat_enabled == null ? 'globalis' : (s.sajat_enabled ? 'be' : 'ki'); }

  async allapotValt(s: any, ertek: string) {
    const enabled = ertek === 'globalis' ? null : (ertek === 'be' ? 1 : 0);
    try {
      await this.put(`/szinkron-beallitas/${this.aktiv.ugyfelId}/${s.forras}`,
        { enabled, intervallum_perc: enabled == null ? null : (s.sajat_perc ?? s.intervallum_perc) });
      await this.betolt(); await this.globalisBetolt();
    } catch { this.hiba('Nem sikerült menteni az időzítést.'); await this.betolt(); }
  }

  async percMent(s: any) {
    if (s.sajat_enabled == null) return;   // öröklött sornál az intervallum is a globálisé
    try { await this.put(`/szinkron-beallitas/${this.aktiv.ugyfelId}/${s.forras}`, { enabled: s.sajat_enabled, intervallum_perc: s.intervallum_perc }); this.ok('Időzítés mentve.'); }
    catch { this.hiba('Nem sikerült menteni az időzítést.'); await this.betolt(); }
  }

  /** Az áttekintő táblázat sorainak szűrése (forrás + csak a problémásak). */
  get szurtAttekintes(): any[] {
    return this.attekintes.filter((s) => (!this.szuroForras || s.forras === this.szuroForras)
      && (!this.csakProblemas || (s.enabled === 1 && s.elofeltetel === false)));
  }

  async felulirTorol(s: any) {
    try {
      await this.gdel(`/felulir/${s.ugyfel_id}/${s.forras}`);
      await this.globalisBetolt(); await this.betolt();
      this.ok('Visszaállt a globális beállításra.');
    } catch { this.hiba('Nem sikerült visszaállítani.'); }
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2200, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2800, color: 'danger' })).present(); }
}
